// Utility to conditionally load fingerprint scripts across the app
// - Loads both open-source and premium fingerprint scripts on page load, on any button click, and on explicit calls
// - Skips loading when localStorage already contains the fingerprint ids
// - Supports both open-source (openSourceFpId) and premium (premiumFpId) fingerprinting

export const FINGERPRINT_LS_KEY = 'openSourceFpId';
export const PREMIUM_FINGERPRINT_LS_KEY = 'visitorId';

const log = (...args: unknown[]) => {
  try {
    // Toggle detailed logs by setting window.__FP_DEBUG = true
    const enabled = (window as any).__FP_DEBUG ?? true; // default to true per request
    if (enabled) {
      // eslint-disable-next-line no-console
      console.info('[fp]', ...args);
    }
  } catch {
    // no-op
  }
};

let loadingPromise: Promise<void> | null = null;
let premiumLoadingPromise: Promise<void> | null = null;
let scriptLoaded = false;
let premiumScriptLoaded = false;
let startupAttempted = false;

const getScriptUrl = (): string | null => {
  // Prefer dedicated env var, then fallbacks for flexibility
  const url =
    process.env.REACT_APP_FP_SCRIPT_URL ||
    process.env.REACT_APP_EXTERNAL_SCRIPT_URL ||
    (window as any).__FP_SCRIPT_URL ||
    (window as any).__EXTERNAL_SCRIPT_URL ||
    null;
  log('getScriptUrl ->', url);
  return url || null;
};

const getPremiumScriptUrl = (): string | null => {
  // Prefer dedicated env var, then fallbacks for flexibility
  const url =
    process.env.REACT_APP_PREMIUM_SCRIPT_URL
  log('getPremiumScriptUrl ->', url);
  return url || null;
};

export const hasFingerprint = (): boolean => {
  try {
    const present = Boolean(window.localStorage.getItem(FINGERPRINT_LS_KEY));
    log('hasFingerprint ->', present);
    return present;
  } catch {
    return false;
  }
};

export const hasPremiumFingerprint = (): boolean => {
  try {
    const present = Boolean(window.localStorage.getItem(PREMIUM_FINGERPRINT_LS_KEY));
    log('hasPremiumFingerprint ->', present);
    return present;
  } catch {
    return false;
  }
};

export const loadFingerprintIfNeeded = async (reason: string = 'unspecified'): Promise<void> => {
  log('loadFingerprintIfNeeded: start, reason =', reason);
  
  // Track last event time for specific events
  if (reason !== 'route-change') {
    (window as any).__lastFpEventTime = Date.now();
  }

  // Only proceed for specific events
  const validReasons = [
    'startup',
    'startup-retry',  // Special case for 6s retry
    //'add-to-cart',
    'cart-click',
    'proceed-to-checkout',
    'checkout-submit',
    'checkout-mount'
  ];
  
  if (!validReasons.includes(reason)) {
    log('skip load: not a trigger event:', reason);
    return Promise.resolve();
  }

  // For startup, track attempt and reset after 10s
  if (reason === 'startup') {
    if (startupAttempted) {
      log('skip load: startup already attempted');
      return Promise.resolve();
    }
    startupAttempted = true;
    // Always reset startupAttempted after 10s to handle manual localStorage clearing
    setTimeout(() => {
      log('resetting startupAttempted after 10s');
      startupAttempted = false;
    }, 10000);
  }

  // Helper to wait for localStorage to be populated
  const waitForStorage = async (maxWaitMs: number = 6000): Promise<boolean> => {
    const start = Date.now();
    while (Date.now() - start < maxWaitMs) {
      const hasId = window.localStorage.getItem('openSourceFpId');
      const hasFlags = window.localStorage.getItem('openSourceFpFlags');
      const hasPremiumId = window.localStorage.getItem('visitorId');
      
      // Check if we have both open source and premium data
      const hasOpenSource = hasId && hasFlags;
      const hasPremium = hasPremiumId;
      
      if (hasOpenSource) {
        log('storage populated:', { 
          hasOpenSource, 
          hasPremium,
          hasId: Boolean(hasId),
          hasFlags: Boolean(hasFlags),
          hasPremiumId: Boolean(hasPremiumId)
        });
        return true;
      }
      await new Promise(r => setTimeout(r, 50));
    }
    log('storage wait timeout:', { 
      hasId: Boolean(window.localStorage.getItem('openSourceFpId')),
      hasFlags: Boolean(window.localStorage.getItem('openSourceFpFlags')),
      hasPremiumId: Boolean(window.localStorage.getItem('visitorId'))
    });
    return false;
  };

  // Clean up any existing scripts
  document.querySelectorAll('script[data-injected="fp-script"]').forEach(el => {
    log('removing existing open source script tag');
    el.parentElement?.removeChild(el);
  });
  document.querySelectorAll('script[data-injected="premium-fp-script"]').forEach(el => {
    log('removing existing premium script tag');
    el.parentElement?.removeChild(el);
  });
  loadingPromise = null;
  premiumLoadingPromise = null;
  scriptLoaded = false;
  premiumScriptLoaded = false;

  // Prevent concurrent loads
  if (loadingPromise || premiumLoadingPromise) {
    log('dedupe: load already in progress');
    return Promise.all([loadingPromise || Promise.resolve(), premiumLoadingPromise || Promise.resolve()]).then(() => {});
  }

  const url = getScriptUrl();
  const premiumUrl = getPremiumScriptUrl();
  
  if (!url && !premiumUrl) {
    // No scripts configured; nothing to do
    log('skip load: no script URLs configured');
    return Promise.resolve();
  }

  // Remove any existing script tags and force fresh injection
  document.querySelectorAll('script[data-injected="fp-script"]').forEach(el => {
    log('removing existing open source script tag');
    el.parentElement?.removeChild(el);
  });
  document.querySelectorAll('script[data-injected="premium-fp-script"]').forEach(el => {
    log('removing existing premium script tag');
    el.parentElement?.removeChild(el);
  });

  // Create promises for both scripts
  const promises: Promise<void>[] = [];

  // Load open source script if URL is available
  if (url) {
    const openSourcePromise = new Promise<void>(async (resolve) => {
      log('injecting open source script tag with src =', url);
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-injected', 'fp-script');
      script.addEventListener('load', async () => {
        log('open source script load event fired');
        scriptLoaded = true;
        script.setAttribute('data-loaded', 'true');
        resolve();
      }, { once: true });
      script.addEventListener('error', () => {
        log('open source script error event fired');
        resolve();
      }, { once: true });
      document.head.appendChild(script);
    });
    loadingPromise = openSourcePromise;
    promises.push(openSourcePromise);
  }

  // Load premium script if URL is available
  if (premiumUrl) {
    const premiumPromise = new Promise<void>(async (resolve) => {
      log('injecting premium script tag with src =', premiumUrl);
      const script = document.createElement('script');
      script.src = premiumUrl;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-injected', 'premium-fp-script');
      script.addEventListener('load', async () => {
        log('premium script load event fired');
        premiumScriptLoaded = true;
        script.setAttribute('data-loaded', 'true');
        resolve();
      }, { once: true });
      script.addEventListener('error', () => {
        log('premium script error event fired');
        resolve();
      }, { once: true });
      document.head.appendChild(script);
    });
    premiumLoadingPromise = premiumPromise;
    promises.push(premiumPromise);
  }

  // Wait for all scripts to load, then check storage
  const promise = Promise.all(promises).then(async () => {
    log('all scripts loaded, waiting for storage');
    await waitForStorage(2000);
    log('load cycle complete');
  });

  // Reset loading guards when complete
  promise.finally(() => {
    log('load cycle complete, resetting guards');
    loadingPromise = null;
    premiumLoadingPromise = null;
  });

  return promise;
};

export const initFingerprintAutoload = (): void => {
  if ((window as any).__fpAutoloadInit) return;
  (window as any).__fpAutoloadInit = true;

  // Initial load attempt
  const startupLoad = async () => {
    try {
      await loadFingerprintIfNeeded('startup');
      
      // After 6 seconds, check if we got localStorage values
      setTimeout(async () => {
        try {
          const hasId = window.localStorage.getItem('openSourceFpId');
          const hasFlags = window.localStorage.getItem('openSourceFpFlags');
          
          const hasOpenSource = hasId && hasFlags;
          
          if (!hasOpenSource) {
            log('open-source storage still empty after 6s, retrying startup load');
            scriptLoaded = false; // Reset the flags to force fresh injection
            premiumScriptLoaded = false;
            await loadFingerprintIfNeeded('startup-retry');
          }
        } catch (e) {
          log('error in retry check:', e);
        }
      }, 6000);
    } catch (e) {
      log('error in startup load:', e);
    }
  };

  // Only attempt startup load if not already attempted
  if (!startupAttempted) {
    void startupLoad();
  }
};